import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Clock, CheckCircle, XCircle, BookOpen, Monitor, Smartphone, Volume2, AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  STUDYING: 'studying',
  QUESTIONS: 'questions',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Best for reading</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Fisher-Yates shuffle
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Rule sets with correct answer marked as object property
const RULE_SETS = {
  1: [
    {
      title: "Platform Dispatch Procedures",
      content: `Before dispatching a train, staff must complete the following checks in sequence:

1. DOOR CHECK: Verify all doors show closed on the Door Proving System (DPS). The indicator must show GREEN for at least 3 seconds.

2. PLATFORM CLEAR: Confirm no passengers are attempting to board. Use mirrors AND CCTV where fitted.

3. SIGNAL CHECK: The starting signal must display a proceed aspect (GREEN or DOUBLE YELLOW).

4. WHISTLE/HORN: Sound two short whistles to alert platform staff.

5. RA BUTTON: Press the Right Away (RA) button within 5 seconds of the whistle.

If any check fails, DO NOT dispatch. Report to the Train Operations Controller (TOC) on Radio Channel 2.

Platform edge monitors must be checked every 15 minutes during service hours.`,
      questions: [
        {
          question: "How long must the DPS show GREEN before dispatch?",
          options: [
            { text: "1 second", correct: false },
            { text: "3 seconds", correct: true },
            { text: "5 seconds", correct: false },
            { text: "10 seconds", correct: false }
          ]
        },
        {
          question: "What TWO things must be used to check the platform is clear?",
          options: [
            { text: "Mirrors and radio", correct: false },
            { text: "Mirrors and CCTV", correct: true },
            { text: "CCTV and whistle", correct: false },
            { text: "Radio and CCTV", correct: false }
          ]
        },
        {
          question: "How many short whistles are sounded before dispatch?",
          options: [
            { text: "One", correct: false },
            { text: "Two", correct: true },
            { text: "Three", correct: false },
            { text: "Four", correct: false }
          ]
        },
        {
          question: "Within how many seconds must the RA button be pressed after the whistle?",
          options: [
            { text: "3 seconds", correct: false },
            { text: "5 seconds", correct: true },
            { text: "10 seconds", correct: false },
            { text: "15 seconds", correct: false }
          ]
        },
        {
          question: "If a check fails, who must be contacted?",
          options: [
            { text: "Station Manager", correct: false },
            { text: "Train Operations Controller (TOC)", correct: true },
            { text: "Driver directly", correct: false },
            { text: "Platform staff", correct: false }
          ]
        }
      ]
    },
    {
      title: "Track Circuit Block Regulations",
      content: `Track circuits detect train presence. The following rules apply:

TRACK CIRCUIT FAILURE INDICATIONS:
• Track Circuit Failure (TCF) shows as a RED occupation on the signaller's panel
• "TC FAIL" message appears on the Train Describers
• Affected section shows as OCCUPIED even when empty

DRIVER ACTIONS FOR TCF:
1. Stop at the signal protecting the affected section
2. Contact signaller via Signal Post Telephone (SPT) or GSM-R
3. Wait for verbal authority before proceeding
4. If authorised, proceed at CAUTION (maximum 15 mph) through the section
5. Report clear of section to signaller

SIGNALLER MUST CONFIRM:
• Line is clear by last train report
• At least 5 minutes since last train passed
• No engineering work in progress on that section`,
      questions: [
        {
          question: "What colour shows on the signaller's panel during a TC failure?",
          options: [
            { text: "Yellow", correct: false },
            { text: "Red", correct: true },
            { text: "White", correct: false },
            { text: "Blue", correct: false }
          ]
        },
        {
          question: "What is the maximum speed when proceeding under caution through a TC failure?",
          options: [
            { text: "5 mph", correct: false },
            { text: "15 mph", correct: true },
            { text: "25 mph", correct: false },
            { text: "40 mph", correct: false }
          ]
        },
        {
          question: "How should the driver contact the signaller?",
          options: [
            { text: "Mobile phone", correct: false },
            { text: "Signal Post Telephone or GSM-R", correct: true },
            { text: "Radio Channel 1", correct: false },
            { text: "Wait for signaller to call", correct: false }
          ]
        },
        {
          question: "How long must have passed since the last train before the signaller can authorise movement?",
          options: [
            { text: "2 minutes", correct: false },
            { text: "5 minutes", correct: true },
            { text: "10 minutes", correct: false },
            { text: "15 minutes", correct: false }
          ]
        },
        {
          question: "What message appears on Train Describers during a TC failure?",
          options: [
            { text: "TRACK BLOCKED", correct: false },
            { text: "TC FAIL", correct: true },
            { text: "SECTION OCCUPIED", correct: false },
            { text: "SIGNAL FAULT", correct: false }
          ]
        }
      ]
    },
    {
      title: "AWS/TPWS Acknowledgement Rules",
      content: `The Automatic Warning System (AWS) requires driver acknowledgement:

AWS HORN SOUNDS:
• At every signal displaying CAUTION (single yellow) or DANGER (red)
• At Permanent Speed Restrictions (PSR)
• At Temporary Speed Restrictions (TSR)
• Approaching buffer stops

ACKNOWLEDGEMENT PROCEDURE:
1. AWS horn sounds automatically at the AWS magnet (200 yards before signal)
2. Driver must press the AWS button within 2.5 seconds
3. If not acknowledged, brakes apply automatically

SUNFLOWER INDICATOR:
• All BLACK = System active, clear signals passed
• BLACK and YELLOW = Caution acknowledged, restriction ahead
• Flashing = System fault - report to Control

If AWS fails completely, maximum speed is reduced to 40 mph until the next AWS-fitted signal is passed at green.`,
      questions: [
        {
          question: "Within how many seconds must the driver acknowledge the AWS horn?",
          options: [
            { text: "1.5 seconds", correct: false },
            { text: "2.5 seconds", correct: true },
            { text: "4 seconds", correct: false },
            { text: "5 seconds", correct: false }
          ]
        },
        {
          question: "How far before the signal is the AWS magnet located?",
          options: [
            { text: "100 yards", correct: false },
            { text: "200 yards", correct: true },
            { text: "300 yards", correct: false },
            { text: "400 yards", correct: false }
          ]
        },
        {
          question: "What does a BLACK and YELLOW sunflower indicate?",
          options: [
            { text: "System fault", correct: false },
            { text: "Clear signal passed", correct: false },
            { text: "Caution acknowledged, restriction ahead", correct: true },
            { text: "Emergency braking", correct: false }
          ]
        },
        {
          question: "If AWS fails completely, what is the maximum speed?",
          options: [
            { text: "20 mph", correct: false },
            { text: "40 mph", correct: true },
            { text: "60 mph", correct: false },
            { text: "Normal line speed", correct: false }
          ]
        },
        {
          question: "What happens if the AWS horn is NOT acknowledged?",
          options: [
            { text: "Warning light illuminates", correct: false },
            { text: "Brakes apply automatically", correct: true },
            { text: "Signal changes to red", correct: false },
            { text: "Control is notified", correct: false }
          ]
        }
      ]
    },
    {
      title: "Defective Train Procedures",
      content: `When a defect is detected during service, follow this procedure:

IMMEDIATE ACTIONS:
1. Assess if the train can continue safely to the next station
2. If safe, proceed at reduced speed (maximum 30 mph for minor defects)
3. Report to Control using the defect code system

DEFECT CODES:
• Code A: Minor - Train can complete journey with monitoring
• Code B: Moderate - Detrain passengers at next suitable station
• Code C: Severe - Stop immediately, do not move train

DETRAINING REQUIREMENTS:
• Must be authorised by Control
• Adjacent lines must be blocked (confirmed by signaller)
• Staff must be present on platform
• Mobility-impaired passengers assisted first

Train must not be moved after a Code C defect until examined by qualified maintenance staff.`,
      questions: [
        {
          question: "What is the maximum speed for minor defects?",
          options: [
            { text: "15 mph", correct: false },
            { text: "30 mph", correct: true },
            { text: "45 mph", correct: false },
            { text: "60 mph", correct: false }
          ]
        },
        {
          question: "What does a Code B defect require?",
          options: [
            { text: "Continue with monitoring", correct: false },
            { text: "Detrain passengers at next suitable station", correct: true },
            { text: "Stop immediately", correct: false },
            { text: "No action required", correct: false }
          ]
        },
        {
          question: "Who must authorise detraining?",
          options: [
            { text: "Station Manager", correct: false },
            { text: "Driver", correct: false },
            { text: "Control", correct: true },
            { text: "Signaller", correct: false }
          ]
        },
        {
          question: "What must be blocked during detraining?",
          options: [
            { text: "Platform entrance", correct: false },
            { text: "Adjacent lines", correct: true },
            { text: "Station exits", correct: false },
            { text: "Signal box", correct: false }
          ]
        },
        {
          question: "Who must be assisted first during detraining?",
          options: [
            { text: "Elderly passengers", correct: false },
            { text: "Mobility-impaired passengers", correct: true },
            { text: "Children", correct: false },
            { text: "Staff", correct: false }
          ]
        }
      ]
    }
  ],
  2: [
    {
      title: "Speed Restriction Compliance",
      content: `Temporary and permanent speed restrictions must be followed precisely:

TEMPORARY SPEED RESTRICTIONS (TSR):
• TSRs are marked with yellow diamond boards
• Speed limit displayed in black numbers
• 'C' board marks commencement
• 'T' board marks termination
• Resume normal speed only after passing the T board

PERMANENT SPEED RESTRICTIONS (PSR):
• Shown on circular yellow signs with black border
• Apply continuously at that location
• May be combined with other restrictions

EMERGENCY SPEED RESTRICTIONS (ESR):
• Implemented by Control verbally via radio
• Maximum 20 mph unless otherwise stated
• Must be confirmed back to Control`,
      questions: [
        {
          question: "What colour are Temporary Speed Restriction boards?",
          options: [
            { text: "Red", correct: false },
            { text: "Yellow diamond", correct: true },
            { text: "Green", correct: false },
            { text: "White", correct: false }
          ]
        },
        {
          question: "What letter marks the start of a TSR?",
          options: [
            { text: "S", correct: false },
            { text: "B", correct: false },
            { text: "C", correct: true },
            { text: "A", correct: false }
          ]
        },
        {
          question: "What letter marks the end of a TSR?",
          options: [
            { text: "E", correct: false },
            { text: "T", correct: true },
            { text: "F", correct: false },
            { text: "X", correct: false }
          ]
        },
        {
          question: "What shape indicates a permanent speed restriction?",
          options: [
            { text: "Square", correct: false },
            { text: "Triangle", correct: false },
            { text: "Circle", correct: true },
            { text: "Diamond", correct: false }
          ]
        },
        {
          question: "When can you resume normal speed after a TSR?",
          options: [
            { text: "After 100 metres", correct: false },
            { text: "After passing the T board", correct: true },
            { text: "When you see a green signal", correct: false },
            { text: "After 30 seconds", correct: false }
          ]
        }
      ]
    },
    {
      title: "Door Operation Safety",
      content: `Door operations must follow strict safety procedures:

• Doors can only be released when the train is at a complete standstill
• Platform side doors only - never release doors on the non-platform side
• Door release must be confirmed by the guard using 2 short buzzer signals
• Allow minimum 20 seconds for passenger boarding before door closure
• Sound the door closure warning 5 seconds before closing
• Check all door indicator lights show green before departure`,
      questions: [
        {
          question: "When can doors be released?",
          options: [
            { text: "When slowing down", correct: false },
            { text: "When at complete standstill", correct: true },
            { text: "When approaching platform", correct: false },
            { text: "At any time", correct: false }
          ]
        },
        {
          question: "How does the guard confirm door release?",
          options: [
            { text: "1 long buzz", correct: false },
            { text: "2 short buzzer signals", correct: true },
            { text: "Radio call", correct: false },
            { text: "Hand signal", correct: false }
          ]
        },
        {
          question: "Minimum boarding time before door closure?",
          options: [
            { text: "10 seconds", correct: false },
            { text: "15 seconds", correct: false },
            { text: "20 seconds", correct: true },
            { text: "30 seconds", correct: false }
          ]
        },
        {
          question: "How long before closure is the warning sounded?",
          options: [
            { text: "3 seconds", correct: false },
            { text: "5 seconds", correct: true },
            { text: "10 seconds", correct: false },
            { text: "15 seconds", correct: false }
          ]
        },
        {
          question: "What colour must all door indicators show before departure?",
          options: [
            { text: "Red", correct: false },
            { text: "Yellow", correct: false },
            { text: "Green", correct: true },
            { text: "White", correct: false }
          ]
        }
      ]
    },
    {
      title: "Defect Reporting Basics",
      content: `All train defects must be reported correctly:

• Minor defects are reported using form TDR-101
• Major defects require immediate radio contact with Control
• The defect code must be recorded from the fault display panel
• Location must include the nearest marker post number
• Time of defect occurrence must be noted to the nearest minute
• Driver's staff number must be included on all defect reports`,
      questions: [
        {
          question: "Which form is used for minor defects?",
          options: [
            { text: "TDR-100", correct: false },
            { text: "TDR-101", correct: true },
            { text: "TDR-102", correct: false },
            { text: "TDR-103", correct: false }
          ]
        },
        {
          question: "How are major defects reported?",
          options: [
            { text: "Written form only", correct: false },
            { text: "Immediate radio to Control", correct: true },
            { text: "At end of shift", correct: false },
            { text: "Email to depot", correct: false }
          ]
        },
        {
          question: "Where is the defect code found?",
          options: [
            { text: "Rule book", correct: false },
            { text: "Fault display panel", correct: true },
            { text: "Control room", correct: false },
            { text: "Driver's manual", correct: false }
          ]
        },
        {
          question: "What location reference is required?",
          options: [
            { text: "Station name", correct: false },
            { text: "Nearest marker post number", correct: true },
            { text: "GPS coordinates", correct: false },
            { text: "Mile number", correct: false }
          ]
        },
        {
          question: "To what precision must time be recorded?",
          options: [
            { text: "Nearest hour", correct: false },
            { text: "Nearest 5 minutes", correct: false },
            { text: "Nearest minute", correct: true },
            { text: "Nearest second", correct: false }
          ]
        }
      ]
    }
  ],
  2: [
    {
      title: "Signal Response Procedures",
      content: `When encountering signals, drivers must adhere to these procedures:

• A red signal means stop immediately and do not proceed
• A yellow signal indicates caution - reduce speed to 40mph
• A double yellow signal means prepare to stop at the next signal
• A green signal allows normal line speed
• If a signal is defective, contact the signaller immediately using form RT3185
• Never pass a red signal without explicit authorisation from the signaller
• Record all signal defects in the Driver's Log Book within 5 minutes`,
      questions: [
        {
          question: "What speed must you reduce to when seeing a yellow signal?",
          options: [
            { text: "30mph", correct: false },
            { text: "40mph", correct: true },
            { text: "50mph", correct: false },
            { text: "60mph", correct: false }
          ]
        },
        {
          question: "What does a double yellow signal indicate?",
          options: [
            { text: "Proceed normally", correct: false },
            { text: "Prepare to stop at next signal", correct: true },
            { text: "Stop immediately", correct: false },
            { text: "Reduce to 20mph", correct: false }
          ]
        },
        {
          question: "Which form is used to report a defective signal?",
          options: [
            { text: "RT3180", correct: false },
            { text: "RT3185", correct: true },
            { text: "RT3190", correct: false },
            { text: "RT3195", correct: false }
          ]
        },
        {
          question: "Within how many minutes must signal defects be recorded?",
          options: [
            { text: "3 minutes", correct: false },
            { text: "5 minutes", correct: true },
            { text: "10 minutes", correct: false },
            { text: "15 minutes", correct: false }
          ]
        },
        {
          question: "Who must authorise passing a red signal?",
          options: [
            { text: "Control Centre", correct: false },
            { text: "The signaller", correct: true },
            { text: "Station manager", correct: false },
            { text: "Train guard", correct: false }
          ]
        }
      ]
    },
    {
      title: "Passenger Emergency Procedures",
      content: `In the event of a passenger emergency:

• Press the emergency stop button only if there is immediate danger
• Contact the guard using the internal communication system
• The guard must inform Control within 2 minutes of any incident
• If medical assistance is required, request it at the next station
• Document all incidents using form PE2500
• Passenger details must be recorded if they provide consent
• Emergency services should be contacted using the dedicated emergency phone`,
      questions: [
        {
          question: "When should the emergency stop button be pressed?",
          options: [
            { text: "For any incident", correct: false },
            { text: "Only if immediate danger", correct: true },
            { text: "When requested by passenger", correct: false },
            { text: "At every stop", correct: false }
          ]
        },
        {
          question: "Within how many minutes must the guard inform Control?",
          options: [
            { text: "1 minute", correct: false },
            { text: "2 minutes", correct: true },
            { text: "5 minutes", correct: false },
            { text: "10 minutes", correct: false }
          ]
        },
        {
          question: "Which form is used to document incidents?",
          options: [
            { text: "PE2000", correct: false },
            { text: "PE2500", correct: true },
            { text: "PE3000", correct: false },
            { text: "PE3500", correct: false }
          ]
        },
        {
          question: "Where should medical assistance be requested?",
          options: [
            { text: "Immediately on train", correct: false },
            { text: "At the next station", correct: true },
            { text: "At the final destination", correct: false },
            { text: "At the depot", correct: false }
          ]
        },
        {
          question: "How should emergency services be contacted?",
          options: [
            { text: "Personal mobile phone", correct: false },
            { text: "Dedicated emergency phone", correct: true },
            { text: "Passenger intercom", correct: false },
            { text: "Radio channel 1", correct: false }
          ]
        }
      ]
    },
    {
      title: "Level Crossing Procedures",
      content: `When approaching level crossings, strict procedures apply:

• Sound horn at 400 metres before any open level crossing
• Reduce speed to 40mph when approaching automatic half-barriers
• If barriers fail to lower, stop and contact signaller on channel 3
• User-worked crossings require stopping and checking before proceeding
• Never enter a crossing if the exit is not clear
• Report any crossing defects using form LC-245`,
      questions: [
        {
          question: "At what distance should you sound the horn?",
          options: [
            { text: "200 metres", correct: false },
            { text: "300 metres", correct: false },
            { text: "400 metres", correct: true },
            { text: "500 metres", correct: false }
          ]
        },
        {
          question: "What speed when approaching automatic half-barriers?",
          options: [
            { text: "30mph", correct: false },
            { text: "40mph", correct: true },
            { text: "50mph", correct: false },
            { text: "Line speed", correct: false }
          ]
        },
        {
          question: "Which channel to contact signaller for barrier failure?",
          options: [
            { text: "Channel 1", correct: false },
            { text: "Channel 2", correct: false },
            { text: "Channel 3", correct: true },
            { text: "Channel 4", correct: false }
          ]
        },
        {
          question: "What must you do at user-worked crossings?",
          options: [
            { text: "Proceed at caution", correct: false },
            { text: "Stop and check before proceeding", correct: true },
            { text: "Sound horn only", correct: false },
            { text: "Contact signaller", correct: false }
          ]
        },
        {
          question: "Which form reports crossing defects?",
          options: [
            { text: "LC-240", correct: false },
            { text: "LC-245", correct: true },
            { text: "LC-250", correct: false },
            { text: "LC-255", correct: false }
          ]
        }
      ]
    },
    {
      title: "Brake System Checks",
      content: `Daily brake checks are mandatory before service:

• Air brake pressure must read between 4.8 and 5.2 bar
• Emergency brake test requires full stop from 20mph within 45 metres
• Parking brake must hold on a 1 in 50 gradient
• Brake pad thickness minimum is 15mm
• All brake checks must be logged in form BC-100
• Failed brake checks mean the train is not fit for service`,
      questions: [
        {
          question: "What is the minimum air brake pressure?",
          options: [
            { text: "4.5 bar", correct: false },
            { text: "4.8 bar", correct: true },
            { text: "5.0 bar", correct: false },
            { text: "5.5 bar", correct: false }
          ]
        },
        {
          question: "Emergency brake test stopping distance from 20mph?",
          options: [
            { text: "35 metres", correct: false },
            { text: "45 metres", correct: true },
            { text: "55 metres", correct: false },
            { text: "65 metres", correct: false }
          ]
        },
        {
          question: "Parking brake must hold on what gradient?",
          options: [
            { text: "1 in 30", correct: false },
            { text: "1 in 40", correct: false },
            { text: "1 in 50", correct: true },
            { text: "1 in 60", correct: false }
          ]
        },
        {
          question: "Minimum brake pad thickness?",
          options: [
            { text: "10mm", correct: false },
            { text: "15mm", correct: true },
            { text: "20mm", correct: false },
            { text: "25mm", correct: false }
          ]
        },
        {
          question: "Which form logs brake checks?",
          options: [
            { text: "BC-50", correct: false },
            { text: "BC-100", correct: true },
            { text: "BC-150", correct: false },
            { text: "BC-200", correct: false }
          ]
        }
      ]
    },
    {
      title: "Weather Condition Protocols",
      content: `Adverse weather requires specific operational adjustments:

• When visibility drops below 200 metres, reduce speed by 20%
• In heavy rain, increase braking distance calculation by 15%
• Snow or ice conditions require activating sanders at stations
• Wind speeds above 60mph require speed restriction to 50mph maximum
• Fog warning lights at signals must be reported to Control
• All weather-related incidents logged on form WC-300`,
      questions: [
        {
          question: "At what visibility must speed be reduced?",
          options: [
            { text: "100 metres", correct: false },
            { text: "150 metres", correct: false },
            { text: "200 metres", correct: true },
            { text: "250 metres", correct: false }
          ]
        },
        {
          question: "By how much should speed reduce in low visibility?",
          options: [
            { text: "10%", correct: false },
            { text: "15%", correct: false },
            { text: "20%", correct: true },
            { text: "25%", correct: false }
          ]
        },
        {
          question: "Heavy rain increases braking distance by?",
          options: [
            { text: "10%", correct: false },
            { text: "15%", correct: true },
            { text: "20%", correct: false },
            { text: "25%", correct: false }
          ]
        },
        {
          question: "What wind speed triggers 50mph maximum?",
          options: [
            { text: "50mph", correct: false },
            { text: "60mph", correct: true },
            { text: "70mph", correct: false },
            { text: "80mph", correct: false }
          ]
        },
        {
          question: "Which form logs weather incidents?",
          options: [
            { text: "WC-200", correct: false },
            { text: "WC-250", correct: false },
            { text: "WC-300", correct: true },
            { text: "WC-350", correct: false }
          ]
        }
      ]
    }
  ],
  3: [
    {
      title: "Advanced Signal Operations",
      content: `Advanced signal procedures for experienced drivers:

• A flashing yellow signal indicates the next signal may change - prepare to reduce speed
• Proceed on sight means travel at maximum 15mph and be ready to stop
• When approaching a junction, reduce speed to 35mph unless otherwise indicated
• AWS (Automatic Warning System) acknowledgement must occur within 3 seconds
• If AWS fails to sound, treat the signal aspect as if it were red
• Signal post telephone (SPT) is located within 50 metres of each controlled signal
• Contact time with signaller must not exceed 90 seconds unless in emergency`,
      questions: [
        {
          question: "What does a flashing yellow signal indicate?",
          options: [
            { text: "Speed restriction ahead", correct: false },
            { text: "Next signal may change", correct: true },
            { text: "Track work in progress", correct: false },
            { text: "Station approaching", correct: false }
          ]
        },
        {
          question: "What is the maximum speed for 'proceed on sight'?",
          options: [
            { text: "10mph", correct: false },
            { text: "15mph", correct: true },
            { text: "20mph", correct: false },
            { text: "25mph", correct: false }
          ]
        },
        {
          question: "Within how many seconds must AWS be acknowledged?",
          options: [
            { text: "2 seconds", correct: false },
            { text: "3 seconds", correct: true },
            { text: "5 seconds", correct: false },
            { text: "7 seconds", correct: false }
          ]
        },
        {
          question: "What is the junction approach speed unless otherwise indicated?",
          options: [
            { text: "25mph", correct: false },
            { text: "35mph", correct: true },
            { text: "45mph", correct: false },
            { text: "55mph", correct: false }
          ]
        },
        {
          question: "Within what distance is the SPT located from a controlled signal?",
          options: [
            { text: "25 metres", correct: false },
            { text: "50 metres", correct: true },
            { text: "75 metres", correct: false },
            { text: "100 metres", correct: false }
          ]
        }
      ]
    },
    {
      title: "Degraded Mode Operations",
      content: `When operating in degraded conditions:

• If the DRA (Driver's Reminder Appliance) fails, maximum speed is 25mph
• When TPWS (Train Protection Warning System) is isolated, speed limited to 30mph
• Axle counter failure requires immediate contact with signaller
• Hand signaller authority is valid for one movement only
• Emergency speed restriction must be obeyed immediately - no acknowledgement required
• If radio communication fails, use SPT within 3 minutes
• All degraded mode operations must be logged using form DM750`,
      questions: [
        {
          question: "What is the maximum speed when DRA fails?",
          options: [
            { text: "20mph", correct: false },
            { text: "25mph", correct: true },
            { text: "30mph", correct: false },
            { text: "35mph", correct: false }
          ]
        },
        {
          question: "What is the speed limit when TPWS is isolated?",
          options: [
            { text: "25mph", correct: false },
            { text: "30mph", correct: true },
            { text: "35mph", correct: false },
            { text: "40mph", correct: false }
          ]
        },
        {
          question: "How many movements does a hand signaller authority cover?",
          options: [
            { text: "One movement only", correct: true },
            { text: "Two movements", correct: false },
            { text: "Until cancelled", correct: false },
            { text: "For the shift", correct: false }
          ]
        },
        {
          question: "Within how many minutes must SPT be used if radio fails?",
          options: [
            { text: "1 minute", correct: false },
            { text: "3 minutes", correct: true },
            { text: "5 minutes", correct: false },
            { text: "10 minutes", correct: false }
          ]
        },
        {
          question: "Which form is used to log degraded mode operations?",
          options: [
            { text: "DM500", correct: false },
            { text: "DM750", correct: true },
            { text: "DM1000", correct: false },
            { text: "DM1250", correct: false }
          ]
        }
      ]
    },
    {
      title: "Traction Power Management",
      content: `Electric traction power systems require specific knowledge:

• Main circuit breaker must be open before entering neutral sections
• Neutral sections are marked with black and white chevron boards
• Power must not be applied until 50 metres past the end marker
• If power fails, coast to a stop and lower pantograph within 30 seconds
• Overhead line damage requires immediate stop and Control notification
• Never leave cab with pantograph raised`,
      questions: [
        {
          question: "What must be open before neutral sections?",
          options: [
            { text: "Cab door", correct: false },
            { text: "Main circuit breaker", correct: true },
            { text: "Side vents", correct: false },
            { text: "Emergency exits", correct: false }
          ]
        },
        {
          question: "How are neutral sections marked?",
          options: [
            { text: "Red and white stripes", correct: false },
            { text: "Black and white chevrons", correct: true },
            { text: "Yellow diamonds", correct: false },
            { text: "Blue circles", correct: false }
          ]
        },
        {
          question: "How far past end marker before applying power?",
          options: [
            { text: "25 metres", correct: false },
            { text: "50 metres", correct: true },
            { text: "75 metres", correct: false },
            { text: "100 metres", correct: false }
          ]
        },
        {
          question: "If power fails, lower pantograph within?",
          options: [
            { text: "15 seconds", correct: false },
            { text: "30 seconds", correct: true },
            { text: "45 seconds", correct: false },
            { text: "60 seconds", correct: false }
          ]
        },
        {
          question: "When must you never leave the cab?",
          options: [
            { text: "With doors open", correct: false },
            { text: "With pantograph raised", correct: true },
            { text: "With lights on", correct: false },
            { text: "With radio active", correct: false }
          ]
        }
      ]
    },
    {
      title: "AWS and TPWS Operations",
      content: `Automatic Warning System and Train Protection Warning System procedures:

• AWS sunflower shows black when system is active and clear
• AWS horn sounds 1000m before each signal
• AWS must be acknowledged within 2.5 seconds or brakes apply
• TPWS triggers automatic brake application if overspeed detected
• TPWS intervention requires stopping and calling signaller before moving
• Isolation of AWS/TPWS requires Control authority and form AT-500`,
      questions: [
        {
          question: "What colour is AWS sunflower when clear?",
          options: [
            { text: "Yellow", correct: false },
            { text: "Black", correct: true },
            { text: "Green", correct: false },
            { text: "White", correct: false }
          ]
        },
        {
          question: "AWS horn sounds how far before signals?",
          options: [
            { text: "500m", correct: false },
            { text: "750m", correct: false },
            { text: "1000m", correct: true },
            { text: "1500m", correct: false }
          ]
        },
        {
          question: "AWS acknowledgement time limit?",
          options: [
            { text: "1.5 seconds", correct: false },
            { text: "2.0 seconds", correct: false },
            { text: "2.5 seconds", correct: true },
            { text: "3.0 seconds", correct: false }
          ]
        },
        {
          question: "After TPWS intervention, what must you do first?",
          options: [
            { text: "Continue at caution", correct: false },
            { text: "Stop and call signaller", correct: true },
            { text: "Reset and continue", correct: false },
            { text: "Report to Control", correct: false }
          ]
        },
        {
          question: "Which form for AWS/TPWS isolation?",
          options: [
            { text: "AT-400", correct: false },
            { text: "AT-500", correct: true },
            { text: "AT-600", correct: false },
            { text: "AT-700", correct: false }
          ]
        }
      ]
    },
    {
      title: "Depot Movement Rules",
      content: `Movement within depots requires specific procedures:

• Maximum speed in depot areas is 10mph unless signed otherwise
• Yellow walkways must never be obstructed by rolling stock
• Movements must be authorised by Depot Controller via radio
• Shunting signals take priority over driver's judgement
• All points must be visually checked before passing
• Report position every 2 minutes during depot movements`,
      questions: [
        {
          question: "Maximum depot speed unless otherwise signed?",
          options: [
            { text: "5mph", correct: false },
            { text: "10mph", correct: true },
            { text: "15mph", correct: false },
            { text: "20mph", correct: false }
          ]
        },
        {
          question: "What colour are depot walkways?",
          options: [
            { text: "White", correct: false },
            { text: "Yellow", correct: true },
            { text: "Blue", correct: false },
            { text: "Green", correct: false }
          ]
        },
        {
          question: "Who authorises depot movements?",
          options: [
            { text: "Signaller", correct: false },
            { text: "Depot Controller", correct: true },
            { text: "Station manager", correct: false },
            { text: "Guard", correct: false }
          ]
        },
        {
          question: "What takes priority in depot areas?",
          options: [
            { text: "Driver judgement", correct: false },
            { text: "Shunting signals", correct: true },
            { text: "Timetable", correct: false },
            { text: "Radio instructions", correct: false }
          ]
        },
        {
          question: "How often report position during depot movements?",
          options: [
            { text: "Every minute", correct: false },
            { text: "Every 2 minutes", correct: true },
            { text: "Every 5 minutes", correct: false },
            { text: "Every 10 minutes", correct: false }
          ]
        }
      ]
    }
  ],
  4: [
    {
      title: "Complex Speed Restrictions",
      content: `Understanding and applying speed restrictions:

• Temporary Speed Restriction (TSR) boards are diamond-shaped and yellow
• Permanent Speed Restriction (PSR) boards are rectangular and white
• TSR commencement is marked with a "C" board, termination with a "T" board
• When multiple speed restrictions overlap, apply the most restrictive
• Emergency speed restrictions are communicated verbally and must be recorded within 1 minute
• Speed probes check compliance at locations marked with blue diamond signs
• If unsure of any restriction, reduce to 20mph and contact signaller`,
      questions: [
        {
          question: "What shape and colour are TSR boards?",
          options: [
            { text: "Rectangular and white", correct: false },
            { text: "Diamond-shaped and yellow", correct: true },
            { text: "Circular and red", correct: false },
            { text: "Square and orange", correct: false }
          ]
        },
        {
          question: "What letter marks the commencement of a TSR?",
          options: [
            { text: "A", correct: false },
            { text: "C", correct: true },
            { text: "S", correct: false },
            { text: "B", correct: false }
          ]
        },
        {
          question: "Within how long must emergency speed restrictions be recorded?",
          options: [
            { text: "30 seconds", correct: false },
            { text: "1 minute", correct: true },
            { text: "2 minutes", correct: false },
            { text: "5 minutes", correct: false }
          ]
        },
        {
          question: "What colour are speed probe location signs?",
          options: [
            { text: "Red diamond", correct: false },
            { text: "Blue diamond", correct: true },
            { text: "Yellow square", correct: false },
            { text: "Green circle", correct: false }
          ]
        },
        {
          question: "What speed should you reduce to if unsure of a restriction?",
          options: [
            { text: "15mph", correct: false },
            { text: "20mph", correct: true },
            { text: "25mph", correct: false },
            { text: "30mph", correct: false }
          ]
        }
      ]
    },
    {
      title: "Route Knowledge Requirements",
      content: `Route competency and maintenance requirements:

• Drivers must refresh route knowledge every 6 months if not driven
• Night route competency expires after 12 months of no night running
• Diversionary routes require specific competency sign-off
• Route cards must be carried at all times when on unfamiliar routes
• Pilotman assistance is required after 24 months without operating the route
• Competency assessment includes 3 observed trips minimum
• Route hazard updates must be acknowledged within 48 hours of issue`,
      questions: [
        {
          question: "How often must route knowledge be refreshed if not driven?",
          options: [
            { text: "3 months", correct: false },
            { text: "6 months", correct: true },
            { text: "9 months", correct: false },
            { text: "12 months", correct: false }
          ]
        },
        {
          question: "After how many months does night route competency expire?",
          options: [
            { text: "6 months", correct: false },
            { text: "12 months", correct: true },
            { text: "18 months", correct: false },
            { text: "24 months", correct: false }
          ]
        },
        {
          question: "How many observed trips minimum for competency assessment?",
          options: [
            { text: "2 trips", correct: false },
            { text: "3 trips", correct: true },
            { text: "4 trips", correct: false },
            { text: "5 trips", correct: false }
          ]
        },
        {
          question: "After how long without operating a route is pilotman required?",
          options: [
            { text: "18 months", correct: false },
            { text: "24 months", correct: true },
            { text: "30 months", correct: false },
            { text: "36 months", correct: false }
          ]
        },
        {
          question: "Within how many hours must route hazard updates be acknowledged?",
          options: [
            { text: "24 hours", correct: false },
            { text: "48 hours", correct: true },
            { text: "72 hours", correct: false },
            { text: "96 hours", correct: false }
          ]
        }
      ]
    },
    {
      title: "Single Line Working",
      content: `Single line working procedures for track possessions:

• Token or staff must be obtained from signaller before entering single line
• Maximum speed on single line working is 40mph
• Wrong direction running limited to 20mph
• Pilotman must ride on every train movement
• Token must be surrendered at end of single line section
• Form SL-600 must be completed for all single line movements`,
      questions: [
        {
          question: "What must be obtained before entering single line?",
          options: [
            { text: "Radio clearance", correct: false },
            { text: "Token or staff", correct: true },
            { text: "Written authority", correct: false },
            { text: "Depot permission", correct: false }
          ]
        },
        {
          question: "Maximum speed on single line working?",
          options: [
            { text: "25mph", correct: false },
            { text: "30mph", correct: false },
            { text: "40mph", correct: true },
            { text: "50mph", correct: false }
          ]
        },
        {
          question: "Wrong direction running speed limit?",
          options: [
            { text: "10mph", correct: false },
            { text: "15mph", correct: false },
            { text: "20mph", correct: true },
            { text: "25mph", correct: false }
          ]
        },
        {
          question: "Who must ride on every train during SLW?",
          options: [
            { text: "Signaller", correct: false },
            { text: "Pilotman", correct: true },
            { text: "Controller", correct: false },
            { text: "Manager", correct: false }
          ]
        },
        {
          question: "Which form for single line movements?",
          options: [
            { text: "SL-400", correct: false },
            { text: "SL-500", correct: false },
            { text: "SL-600", correct: true },
            { text: "SL-700", correct: false }
          ]
        }
      ]
    },
    {
      title: "Degraded Mode Operations",
      content: `Operating procedures when systems fail:

• If AWS fails, maximum speed is 40mph and every signal must be reported
• If speedometer fails, judge speed using 3-second gap between mileposts at 60mph
• If radio fails, proceed to next station and use platform telephone
• If TPWS fails, proceed at maximum 30mph
• All degraded mode operations require completion of form DM-750
• Control must be notified immediately of any system failure`,
      questions: [
        {
          question: "Maximum speed with AWS failure?",
          options: [
            { text: "30mph", correct: false },
            { text: "40mph", correct: true },
            { text: "50mph", correct: false },
            { text: "60mph", correct: false }
          ]
        },
        {
          question: "With AWS failure, what must be reported?",
          options: [
            { text: "Position every 5 minutes", correct: false },
            { text: "Every signal", correct: true },
            { text: "Station stops only", correct: false },
            { text: "Speed every mile", correct: false }
          ]
        },
        {
          question: "At 60mph, milepost gap is how many seconds?",
          options: [
            { text: "2 seconds", correct: false },
            { text: "3 seconds", correct: true },
            { text: "4 seconds", correct: false },
            { text: "5 seconds", correct: false }
          ]
        },
        {
          question: "Maximum speed with TPWS failure?",
          options: [
            { text: "20mph", correct: false },
            { text: "30mph", correct: true },
            { text: "40mph", correct: false },
            { text: "50mph", correct: false }
          ]
        },
        {
          question: "Which form for degraded mode operations?",
          options: [
            { text: "DM-500", correct: false },
            { text: "DM-650", correct: false },
            { text: "DM-750", correct: true },
            { text: "DM-850", correct: false }
          ]
        }
      ]
    },
    {
      title: "Possession Protection",
      content: `Track possession safety procedures:

• Engineering possessions are protected by detonators at 1/4 mile intervals
• Three detonators indicate a full stop required
• Possession limit boards are red with white diagonal stripe
• Handsignaller authority is required to pass possession limits
• Maximum speed within possessions is 5mph
• Wrong side failures require form WS-800 completion`,
      questions: [
        {
          question: "Detonator spacing for possession protection?",
          options: [
            { text: "1/8 mile", correct: false },
            { text: "1/4 mile", correct: true },
            { text: "1/2 mile", correct: false },
            { text: "1 mile", correct: false }
          ]
        },
        {
          question: "How many detonators indicate full stop?",
          options: [
            { text: "One", correct: false },
            { text: "Two", correct: false },
            { text: "Three", correct: true },
            { text: "Four", correct: false }
          ]
        },
        {
          question: "Possession limit board colours?",
          options: [
            { text: "Yellow with black stripe", correct: false },
            { text: "Red with white diagonal stripe", correct: true },
            { text: "White with red stripe", correct: false },
            { text: "Blue with white stripe", correct: false }
          ]
        },
        {
          question: "Maximum speed within possessions?",
          options: [
            { text: "5mph", correct: true },
            { text: "10mph", correct: false },
            { text: "15mph", correct: false },
            { text: "20mph", correct: false }
          ]
        },
        {
          question: "Which form for wrong side failures?",
          options: [
            { text: "WS-600", correct: false },
            { text: "WS-700", correct: false },
            { text: "WS-800", correct: true },
            { text: "WS-900", correct: false }
          ]
        }
      ]
    }
  ],
  5: [
    {
      title: "Critical Incident Management",
      content: `Procedures for managing critical incidents:

• Fatality protocol: Stop immediately, apply parking brake, turn off traction current
• Do not leave the cab unless specifically instructed by Control
• Preserve evidence by not moving the train unless essential for safety
• First report must be made within 5 minutes to Control using code "RED7"
• Driver welfare officer must be contacted within 30 minutes
• Written statement must be provided within 4 hours of the incident
• Driver must not operate another train for minimum 72 hours without assessment`,
      questions: [
        {
          question: "What code is used for first report of a fatality?",
          options: [
            { text: "RED5", correct: false },
            { text: "RED7", correct: true },
            { text: "RED9", correct: false },
            { text: "RED11", correct: false }
          ]
        },
        {
          question: "Within how many minutes must the first report be made?",
          options: [
            { text: "3 minutes", correct: false },
            { text: "5 minutes", correct: true },
            { text: "10 minutes", correct: false },
            { text: "15 minutes", correct: false }
          ]
        },
        {
          question: "Within how many minutes must driver welfare be contacted?",
          options: [
            { text: "15 minutes", correct: false },
            { text: "30 minutes", correct: true },
            { text: "45 minutes", correct: false },
            { text: "60 minutes", correct: false }
          ]
        },
        {
          question: "Within how many hours must written statement be provided?",
          options: [
            { text: "2 hours", correct: false },
            { text: "4 hours", correct: true },
            { text: "6 hours", correct: false },
            { text: "8 hours", correct: false }
          ]
        },
        {
          question: "Minimum hours before operating again without assessment?",
          options: [
            { text: "48 hours", correct: false },
            { text: "72 hours", correct: true },
            { text: "96 hours", correct: false },
            { text: "120 hours", correct: false }
          ]
        }
      ]
    },
    {
      title: "Multi-System Operations",
      content: `Operating across different signalling systems:

• ETCS Level 2 transitions require acknowledgement within 10 seconds
• Movement Authority expires after 5 minutes if no forward movement
• Override mode (Staff Responsible) limited to maximum 40km/h
• Balise failure must be reported immediately - code BF100
• System changeover point requires stop unless green MA displayed
• Cross-border operations require both national safety certificates
• Data recording must be verified every 2 hours during extended operations`,
      questions: [
        {
          question: "Within how many seconds must ETCS transitions be acknowledged?",
          options: [
            { text: "5 seconds", correct: false },
            { text: "10 seconds", correct: true },
            { text: "15 seconds", correct: false },
            { text: "20 seconds", correct: false }
          ]
        },
        {
          question: "After how many minutes does Movement Authority expire?",
          options: [
            { text: "3 minutes", correct: false },
            { text: "5 minutes", correct: true },
            { text: "10 minutes", correct: false },
            { text: "15 minutes", correct: false }
          ]
        },
        {
          question: "What is the maximum speed in Staff Responsible mode?",
          options: [
            { text: "30km/h", correct: false },
            { text: "40km/h", correct: true },
            { text: "50km/h", correct: false },
            { text: "60km/h", correct: false }
          ]
        },
        {
          question: "What code is used for balise failure?",
          options: [
            { text: "BF50", correct: false },
            { text: "BF100", correct: true },
            { text: "BF150", correct: false },
            { text: "BF200", correct: false }
          ]
        },
        {
          question: "How often must data recording be verified in extended operations?",
          options: [
            { text: "Every hour", correct: false },
            { text: "Every 2 hours", correct: true },
            { text: "Every 3 hours", correct: false },
            { text: "Every 4 hours", correct: false }
          ]
        }
      ]
    },
    {
      title: "SPAD Prevention Procedures",
      content: `Signal Passed at Danger prevention and response:

• SPAD is defined as any movement past a signal without authority
• After a SPAD, stop immediately and apply parking brake
• Do not reverse or move until authorised by signaller
• Initial SPAD report must be made within 5 minutes
• Form SP-999 must be completed within 24 hours
• Driver may be required to submit to drugs and alcohol testing`,
      questions: [
        {
          question: "What defines a SPAD?",
          options: [
            { text: "Passing a yellow signal", correct: false },
            { text: "Movement past signal without authority", correct: true },
            { text: "Excessive speed", correct: false },
            { text: "AWS failure", correct: false }
          ]
        },
        {
          question: "First action after a SPAD?",
          options: [
            { text: "Reverse to safety", correct: false },
            { text: "Stop and apply parking brake", correct: true },
            { text: "Continue to next signal", correct: false },
            { text: "Radio Control", correct: false }
          ]
        },
        {
          question: "Initial SPAD report deadline?",
          options: [
            { text: "Immediately", correct: false },
            { text: "5 minutes", correct: true },
            { text: "15 minutes", correct: false },
            { text: "30 minutes", correct: false }
          ]
        },
        {
          question: "Which form for SPAD reporting?",
          options: [
            { text: "SP-888", correct: false },
            { text: "SP-999", correct: true },
            { text: "SP-100", correct: false },
            { text: "SP-200", correct: false }
          ]
        },
        {
          question: "Form SP-999 deadline?",
          options: [
            { text: "12 hours", correct: false },
            { text: "24 hours", correct: true },
            { text: "48 hours", correct: false },
            { text: "72 hours", correct: false }
          ]
        }
      ]
    },
    {
      title: "Tunnel Emergency Procedures",
      content: `Tunnel emergencies require specific protocols:

• Tunnel emergency alarm activation stops all trains within the tunnel
• Evacuation only when authorised by Control or if fire is confirmed
• Passengers exit towards nearest portal using emergency lighting
• Fire extinguisher locations marked by white triangle with red border
• Communication points located every 100 metres
• Tunnel ventilation can be activated by Control remotely`,
      questions: [
        {
          question: "What does tunnel alarm activation do?",
          options: [
            { text: "Opens emergency exits", correct: false },
            { text: "Stops all trains in tunnel", correct: true },
            { text: "Activates ventilation", correct: false },
            { text: "Calls emergency services", correct: false }
          ]
        },
        {
          question: "When to evacuate passengers?",
          options: [
            { text: "Immediately on stopping", correct: false },
            { text: "When authorised by Control or fire confirmed", correct: true },
            { text: "After 5 minutes", correct: false },
            { text: "At driver's discretion", correct: false }
          ]
        },
        {
          question: "Fire extinguisher location marking?",
          options: [
            { text: "Red circle", correct: false },
            { text: "White triangle with red border", correct: true },
            { text: "Yellow square", correct: false },
            { text: "Green arrow", correct: false }
          ]
        },
        {
          question: "Communication point spacing in tunnels?",
          options: [
            { text: "50 metres", correct: false },
            { text: "100 metres", correct: true },
            { text: "150 metres", correct: false },
            { text: "200 metres", correct: false }
          ]
        },
        {
          question: "Who can activate tunnel ventilation?",
          options: [
            { text: "Driver only", correct: false },
            { text: "Control remotely", correct: true },
            { text: "Emergency services", correct: false },
            { text: "Station staff", correct: false }
          ]
        }
      ]
    },
    {
      title: "Multi-Train Operations",
      content: `Coupling and multi-train working procedures:

• Maximum speed when coupled is 45mph unless otherwise specified
• Brake continuity test required after every coupling
• Coupling must be verified by both drivers before movement
• Inter-unit doors must be secured in coupled working
• Uncoupling only permitted at designated locations
• Form MT-400 required for all multi-train operations`,
      questions: [
        {
          question: "Maximum coupled speed?",
          options: [
            { text: "35mph", correct: false },
            { text: "45mph", correct: true },
            { text: "55mph", correct: false },
            { text: "65mph", correct: false }
          ]
        },
        {
          question: "What test is required after coupling?",
          options: [
            { text: "Power test", correct: false },
            { text: "Brake continuity test", correct: true },
            { text: "Door test", correct: false },
            { text: "Radio test", correct: false }
          ]
        },
        {
          question: "Who must verify coupling?",
          options: [
            { text: "Lead driver only", correct: false },
            { text: "Both drivers", correct: true },
            { text: "Guard only", correct: false },
            { text: "Control", correct: false }
          ]
        },
        {
          question: "Inter-unit doors must be?",
          options: [
            { text: "Open", correct: false },
            { text: "Secured", correct: true },
            { text: "Alarmed", correct: false },
            { text: "Locked open", correct: false }
          ]
        },
        {
          question: "Which form for multi-train operations?",
          options: [
            { text: "MT-200", correct: false },
            { text: "MT-300", correct: false },
            { text: "MT-400", correct: true },
            { text: "MT-500", correct: false }
          ]
        }
      ]
    }
  ]
};

const RulesMemoryGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.DEVICE_SELECT);
  const [deviceMode, setDeviceMode] = useState(null);
  
  // Study time based on level - more time for beginners
  const studyTimeByLevel = {
    1: 180, // 3 minutes for Level 1 (beginners)
    2: 150, // 2.5 minutes for Level 2
    3: 120, // 2 minutes for Level 3
    4: 100, // 1:40 for Level 4
    5: 90,  // 1:30 for Level 5 (experts)
  };
  const initialStudyTime = studyTimeByLevel[level] || 120;
  
  const [studyTimeLeft, setStudyTimeLeft] = useState(initialStudyTime);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const startTime = useRef(Date.now());
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Select rule set for this level and prepare shuffled questions
  const preparedRuleSet = useMemo(() => {
    const levelRuleSets = RULE_SETS[level] || RULE_SETS[1];
    const randomIndex = Math.floor(Math.random() * levelRuleSets.length);
    const selectedRuleSet = levelRuleSets[randomIndex];
    
    // Shuffle options for each question
    return {
      ...selectedRuleSet,
      questions: selectedRuleSet.questions.map(q => ({
        ...q,
        shuffledOptions: shuffleArray(q.options)
      }))
    };
  }, [level]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Study phase timer
  useEffect(() => {
    if (gameState === GAME_STATES.STUDYING) {
      timerRef.current = setInterval(() => {
        setStudyTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setGameState(GAME_STATES.QUESTIONS);
            return 0;
          }
          // Play warning sound at 8 seconds
          if (prev === 9 && audioRef.current) {
            audioRef.current.play().catch(() => {});
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [gameState]);

  const handleDeviceSelect = (mode) => {
    setDeviceMode(mode);
    setGameState(GAME_STATES.READY);
  };

  const startStudying = () => {
    startTime.current = Date.now();
    setStudyTimeLeft(initialStudyTime);
    setGameState(GAME_STATES.STUDYING);
  };

  const startQuestions = () => {
    // User clicked "Start Test Now" early - stop the timer and begin questions
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setGameState(GAME_STATES.QUESTIONS);
  };

  const handleAnswerSelect = (option, displayIndex) => {
    if (showFeedback) return;
    
    setSelectedAnswer(displayIndex);
    setShowFeedback(true);
    
    const isCorrect = option.correct;
    const newAnswer = {
      questionIndex: currentQuestion,
      selectedOption: option,
      displayIndex,
      isCorrect,
      score: isCorrect ? 10 : -5
    };
    
    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);
    
    setTimeout(() => {
      if (currentQuestion + 1 >= preparedRuleSet.questions.length) {
        finishGame(updatedAnswers);
      } else {
        setCurrentQuestion(c => c + 1);
        setSelectedAnswer(null);
        setShowFeedback(false);
      }
    }, 1500);
  };

  const finishGame = (finalAnswers) => {
    setGameState(GAME_STATES.FINISHED);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    const correctCount = finalAnswers.filter(a => a.isCorrect).length;
    const totalScore = finalAnswers.reduce((sum, a) => sum + a.score, 0);
    const maxScore = preparedRuleSet.questions.length * 10;
    const totalQuestions = preparedRuleSet.questions.length;
    const accuracy = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
    
    onComplete({
      score: Math.max(0, Math.round(totalScore)),
      maxScore,
      accuracy,
      correct: correctCount,
      wrong: totalQuestions - correctCount,
      timeSpent,
      answers: finalAnswers
    });
  };

  const restartGame = () => {
    setGameState(GAME_STATES.DEVICE_SELECT);
    setStudyTimeLeft(120);
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setShowFeedback(false);
  };

  // Device selection
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return <DeviceModeSelector onSelect={handleDeviceSelect} />;
  }

  // Ready state
  if (gameState === GAME_STATES.READY) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          {/* Audio element for warning */}
          <audio ref={audioRef} preload="auto">
            <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkI+IeGxnZ3F+iY+NhXlwaGlweIKIiIR9d3NycHJ2fIGEhIJ+enl5eXl7fX+AgYGBgH9+fn5+fn9/gICAgICAgICAgICA" type="audio/wav" />
          </audio>
          
          <div className="text-center max-w-lg mx-auto">
            <div className="w-16 h-16 bg-[#0B1D51]/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-[#0B1D51]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Rules & Procedures
            </h2>
            <p className="text-sm sm:text-base text-[#64748B] mb-1">Level {level}</p>
            <p className="text-xs text-[#FF9F1C] font-medium mb-4 sm:mb-6">
              Develops skills to improve performance on TRP1 Trainability for Rules and Procedures Test
            </p>
            
            <div className="bg-[#F8FAFC] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 text-left">
              <h3 className="font-semibold text-[#0B1D51] mb-2 text-sm sm:text-base">How this works:</h3>
              <ol className="text-xs sm:text-sm text-[#64748B] space-y-2">
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">1.</span>
                  <span>You will be shown <strong>rules and procedures</strong> to memorise</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">2.</span>
                  <span>You have <strong>2 minutes</strong> to study the information</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">3.</span>
                  <span>A <strong>warning sound</strong> plays at 8 seconds remaining</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">4.</span>
                  <span>After the timer ends, the text is <strong>removed</strong> and replaced with questions</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">5.</span>
                  <span>Answer each question based <strong>only</strong> on what you read - no notes allowed!</span>
                </li>
              </ol>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-slate-100 rounded-lg p-3 text-center">
                <Clock className="w-5 h-5 mx-auto mb-1 text-[#0B1D51]" />
                <p className="text-xs text-[#64748B]">Study Time</p>
                <p className="font-bold text-[#0B1D51]">2:00</p>
              </div>
              <div className="bg-slate-100 rounded-lg p-3 text-center">
                <Volume2 className="w-5 h-5 mx-auto mb-1 text-[#FF9F1C]" />
                <p className="text-xs text-[#64748B]">Audio Warning</p>
                <p className="font-bold text-[#0B1D51]">At 8s</p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span><strong>Scoring:</strong> +10 points for correct answers, -5 penalty for incorrect. Focus on accuracy!</span>
              </p>
            </div>
            
            <Button 
              onClick={startStudying}
              size="lg"
              className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
              data-testid="start-game-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Begin Study Phase (2 minutes)
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Studying phase
  if (gameState === GAME_STATES.STUDYING) {
    const isWarning = studyTimeLeft <= 8;
    
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#0B1D51]">Study Phase</h3>
          <Badge 
            variant="outline" 
            className={`text-lg px-4 py-2 font-mono ${isWarning ? 'bg-red-100 text-red-700 border-red-300 animate-pulse' : 'text-[#0B1D51]'}`}
          >
            <Clock className="w-4 h-4 mr-2" />
            {formatTime(studyTimeLeft)}
          </Badge>
        </div>
        
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <motion.div 
            className={`h-full ${isWarning ? 'bg-red-500' : 'bg-[#FF9F1C]'}`}
            initial={{ width: '100%' }}
            animate={{ width: `${(studyTimeLeft / initialStudyTime) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        {isWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-100 border border-red-300 rounded-lg p-3 text-center"
          >
            <p className="text-red-700 font-medium flex items-center justify-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Time almost up! Finish reading now.
            </p>
          </motion.div>
        )}
        
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <h3 className="font-heading text-lg sm:text-xl font-bold text-[#0B1D51] mb-4 pb-2 border-b">
              {preparedRuleSet.title}
            </h3>
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
              {preparedRuleSet.content}
            </div>
          </CardContent>
        </Card>
        
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
          <p className="text-sm text-[#64748B]">
            Read and memorise the rules above. You will be tested after the timer ends.
          </p>
          <Button
            onClick={startQuestions}
            variant="outline"
            className="border-[#0B1D51] text-[#0B1D51] hover:bg-[#0B1D51]/10 whitespace-nowrap"
            data-testid="start-early-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Ready? Start Test Now
          </Button>
        </div>
      </div>
    );
  }
  
  // Questions phase
  if (gameState === GAME_STATES.QUESTIONS) {
    const question = preparedRuleSet.questions[currentQuestion];
    const totalQuestions = preparedRuleSet.questions.length;
    const correctCount = answers.filter(a => a.isCorrect).length;
    
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-[#64748B]">
            Question {currentQuestion + 1} of {totalQuestions}
          </Badge>
          <div className="text-sm text-[#64748B]">
            Correct: <span className="font-bold text-[#2CB67D]">{correctCount}</span>
          </div>
        </div>
        
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-[#0B1D51]"
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-[#0B1D51] mb-6">
              {question.question}
            </h3>
            
            <div className="space-y-3">
              {question.shuffledOptions.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const isCorrect = option.correct;
                const showResult = showFeedback && (isSelected || isCorrect);
                
                let buttonStyle = 'border-slate-200 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5';
                if (showResult) {
                  if (isCorrect) {
                    buttonStyle = 'border-[#2CB67D] bg-[#2CB67D]/10 text-[#2CB67D]';
                  } else if (isSelected && !isCorrect) {
                    buttonStyle = 'border-red-500 bg-red-500/10 text-red-500';
                  }
                }
                
                return (
                  <motion.button
                    key={index}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswerSelect(option, index)}
                    disabled={showFeedback}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all ${buttonStyle} ${showFeedback ? 'cursor-default' : 'cursor-pointer'}`}
                    data-testid={`answer-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-mono text-sm">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="flex-1">{option.text}</span>
                      {showResult && isCorrect && <CheckCircle className="w-5 h-5 text-[#2CB67D]" />}
                      {showResult && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500" />}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <AnimatePresence>
          {showFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`p-4 rounded-lg text-center font-medium ${
                question.shuffledOptions[selectedAnswer]?.correct
                  ? 'bg-[#2CB67D] text-white'
                  : 'bg-red-500 text-white'
              }`}
            >
              {question.shuffledOptions[selectedAnswer]?.correct ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" /> Correct! +10 points
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" /> Incorrect. -5 points
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
  
  // Finished state
  if (gameState === GAME_STATES.FINISHED) {
    const correctCount = answers.filter(a => a.isCorrect).length;
    const wrongCount = answers.filter(a => !a.isCorrect).length;
    const totalScore = Math.max(0, answers.reduce((sum, a) => sum + a.score, 0));
    const accuracy = Math.round((correctCount / preparedRuleSet.questions.length) * 100);
    
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#2CB67D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Test Complete!
            </h2>
            <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mt-6">
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#0B1D51]">{totalScore}</p>
                <p className="text-sm text-[#64748B]">Score</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#2CB67D]">{accuracy}%</p>
                <p className="text-sm text-[#64748B]">Accuracy</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#2CB67D]">{correctCount}</p>
                <p className="text-sm text-[#64748B]">Correct</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#EF4444]">{wrongCount}</p>
                <p className="text-sm text-[#64748B]">Incorrect</p>
              </div>
            </div>
            <Button 
              onClick={restartGame}
              variant="outline"
              className="mt-6"
              data-testid="restart-game-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return null;
};

export default RulesMemoryGame;
